enum MortgageDipStatus {
  INVALID_DIP_STATUS
  DECLINED
  REFER
  ACCEPTED
  PENDING
  REDIRECT
  ERROR
  MANUAL_ACTION_REQUIRED
}

enum AdditionalDipFields {
  MORTGAGE_ACCOUNT_NUMBER
  DATE_PAID_OFF
  PREVIOUS_LENDER_NAME
  CURRENT_RENT
  CLIENT_0_INDUSTRY
  CLIENT_1_INDUSTRY
}

type Dip {
  additional_fields_required: [String]
  messages: [String!]
  dip_available: Boolean
  permanent: Boolean
  reason: String
  mortgage_id: String
  supports_dip_certificate: Boolean
}

input DipInput {
  case_id: ID!
  mortgage_ids: [ID!]!
  existing_mortgage_id: ID
}

input NoPropertyDipInput {
  case_id: ID!
  mortgage_id: ID
  existing_mortgage_id: ID
  lender: String!
}

input DipStatusInput {
  dip_application_id: ID!
  mortgage_id: ID
  case_id: ID
}

input SubmitDipInput {
  mortgage_account_number: String
  previous_lender_name: String
  date_paid_off: String
  current_rent: String
  client0industry: String
  client1industry: String
  client0asylum: Boolean
  client0refugee: Boolean
  client0ilr: Boolean
  client0deposit_own_resources: Boolean
  client0salary_uk_bank: Boolean
  client0work_permit: Boolean
  client0uk_resident: Boolean
  client0lowtravel: String
  client0lowchildcare: String
  client1asylum: Boolean
  client1refugee: Boolean
  client1ilr: Boolean
  client1deposit_own_resources: Boolean
  client1salary_uk_bank: Boolean
  client1work_permit: Boolean
  client1uk_resident: Boolean
  client0diplomat: Boolean
  client1diplomat: Boolean
  client1lowtravel: String
  client1lowchildcare: String
  new_lease_term: Int
  helping_hand: Boolean
  internal_scheme: String
  equity_share_provider: String
  equity_share_repayment_date: String
  equity_share_max_sale_share: Int
  currentRent: String
  mortgageAccountNumber: String
  previousLenderName: String
  datePaidOff: String
  relationship_to_seller: String
}

type SubmitDipFields {
  mortgage_account_number: String
  previous_lender_name: String
  date_paid_off: String
  current_rent: String
  client0industry: String
  client1industry: String
  client0asylum: Boolean
  client0refugee: Boolean
  client0ilr: Boolean
  client0deposit_own_resources: Boolean
  client0salary_uk_bank: Boolean
  client0work_permit: Boolean
  client0uk_resident: Boolean
  client0lowtravel: String
  client0lowchildcare: String
  client1asylum: Boolean
  client1refugee: Boolean
  client1ilr: Boolean
  client1deposit_own_resources: Boolean
  client1salary_uk_bank: Boolean
  client1work_permit: Boolean
  client1uk_resident: Boolean
  client0diplomat: Boolean
  client1diplomat: Boolean
  client1lowtravel: String
  client1lowchildcare: String
  new_lease_term: Int
  helping_hand: Boolean
  internal_scheme: String
  equity_share_provider: String
  equity_share_repayment_date: String
  equity_share_max_sale_share: Int
  currentRent: String
  mortgageAccountNumber: String
  previousLenderName: String
  datePaidOff: String
  relationship_to_seller: String
}

type SubmitDip {
  approved_amount: Int
  id: String
  lender: String
  provider: String
  provider_id: String
  reason: String
  status: String
  details: DipDetails
  available_next_steps: [NextStep!]
  fields: SubmitDipFields
}

union SubmitDipWithApplicationDetails = SubmitDip | ApplicationDetails

enum DipDocumentType {
  certificate
  declaration
}

type ApplicationDetails {
  application_status: ApplicationStatus
  application_fields: ApplicationFields
  completed_steps: [NextStep]
  application_details: ApplicationResponse
}

enum ApplicationStatus {
  ACCEPTED
  REFUSED
  ERROR
}

input ApplicationFieldsInput {
  fee_rebate_amount: String
  applicant_1_income_proof_type: String
  applicant_1_special_contact_needs: String
  applicant_0_income_proof_type: String
  applicant_0_special_contact_needs: String
  solicitor_code: String
  solicitor_name: String
  add_or_remove_applicants: String
  index_of_bank_details: String
  day_for_direct_debit: String
  applicant_for_bank_details: String
  instruct_l2_survey: String
  valuer_name: String
  valuer_company: String
  valuation_date: String
  valuer_postcode: String
  has_basement_or_cellar: String
  plot_size_above_one_acre: String
  agricultural_restrictions: String
  flooded_last_5_years: String
  subsidence: String
  structural_alteration_not_visible: String
  freehold_purchase: String
  property_access_same_as_first_applicant: String
  property_access_contact_name: String
  property_access_company_name: String
  property_access_contact_number: String
  valuer_notes: String
  property_access_address: LutherAddressInput
  proc_fee_refundable_amount: String
  local_authority: String
  still_under_construction: String
}

type ApplicationFields {
  fee_rebate_amount: String
  applicant_1_income_proof_type: String
  applicant_1_special_contact_needs: String
  applicant_0_income_proof_type: String
  applicant_0_special_contact_needs: String
  solicitor_code: String
  solicitor_name: String
  add_or_remove_applicants: String
  index_of_bank_details: String
  day_for_direct_debit: String
  applicant_for_bank_details: String
  instruct_l2_survey: String
  valuer_name: String
  valuer_company: String
  valuation_date: String
  valuer_postcode: String
  has_basement_or_cellar: String
  plot_size_above_one_acre: String
  agricultural_restrictions: String
  flooded_last_5_years: String
  subsidence: String
  structural_alteration_not_visible: String
  freehold_purchase: String
  property_access_same_as_first_applicant: String
  property_access_contact_name: String
  property_access_company_name: String
  property_access_contact_number: String
  valuer_notes: String
  property_access_address: String
  proc_fee_refundable_amount: String
  local_authority: String
  still_under_construction: String
}

enum ApplicationField {
  fee_rebate_amount
  applicant_1_income_proof_type
  applicant_1_special_contact_needs
  applicant_0_income_proof_type
  applicant_0_special_contact_needs
  solicitor_code
  solicitor_name
  add_or_remove_applicants
  index_of_bank_details
  day_for_direct_debit
  applicant_for_bank_details
  instruct_l2_survey
  valuer_name
  valuer_company
  valuation_date
  valuer_postcode
  has_basement_or_cellar
  plot_size_above_one_acre
  agricultural_restrictions
  flooded_last_5_years
  subsidence
  structural_alteration_not_visible
  freehold_purchase
  property_access_same_as_first_applicant
  property_access_contact_name
  property_access_company_name
  property_access_contact_number
  valuer_notes
  property_access_address
  proc_fee_refundable_amount
  local_authority
  still_under_construction
}

type ApplicationResponse {
  id: ID!
  provider_id: ID!
  status: ApplicationStatus
  lender: String!
  provider: String
  approved_amount: Int
  product_code: String!
  requirements: [Requirement!]
  link: String
  warnings: [String!]
}

type Requirement {
  name: String!
  client: String!
  id: ID!
}

type DipDetails {
  decisionDetails: DipDecisionDetails
  decisionExpiryDate: String
  overallDecision: String
  reprocessInd: Boolean
  # Raw per-provider decision results (e.g. Santander/Nationwide input-validation
  # failures). The shape varies by provider, so every field is optional and consumers
  # must guard before rendering.
  decisions: [DipResultDecision!]
}

type DipResultDecision {
  provider: String
  resultType: String
  errorMessages: [DipResultErrorMessage!]
}

type DipResultErrorMessage {
  name: String
  result: String
}

input DipDetailsInput {
  decisionExpiryDate: String
  overallDecision: String
  reprocessInd: Boolean
}

type DipDecisionDetails {
  reasonCodes: [DipDecisionReason!]
  affordabilityDecision: DipDecisionBreakdown
  creditDecision: DipDecisionBreakdown
  policyDecision: DipDecisionBreakdown
  propertyDecision: DipDecisionBreakdown
  bureauDetails: [BureauDetails!]
  proofRequirements: [DipProofRequired]
}

type DipProofRequired {
  applicantNumber: String
  code: String!
  description: String
  name: String!
}

type DipDecisionReason {
  code: String!
  text: String!
}

type DipDecisionBreakdown {
  decision: DipDecisionBreakdownStatus!
  description: String
  maximumAmount: Int
  minimumTermYears: Int
  reasonCodes: [DipDecisionBreakdownReason!]
  helpingHandDecision: String
  helpingHandMinimumTermMonths: Int
  helpingHandMinimumTermYears: Int
  decisionBasedOn: String
  helpingHandMaximumAmount: Int
}

type DipDecisionBreakdownReason {
  code: String
  text: String
}

type BureauDetails {
  bureauName: String
}

enum DipDecisionBreakdownStatus {
  Fail
  Refer
  Pass
  Accept
  Decline
  NotRun
}

type ApplyForDip {
  url: String!
  message: String
}

enum NextStep {
  redirect # Redirect to the provider to continue the application
  reservation # Reserve the funds and redirect to the provider
  full_application # Complete the full application in Acre then optionally redirect
  dip_redirect # Redirect to complete the DIP with FMA in the provider system
  resubmit # The case can be edited by resubmitting
  poll_for_status # The client should poll for status updates from the lender
  none # No FMA available
  get_dip_certificate # Download DIP certificate
  get_mortgage_declaration # Download mortgage declaration document
}

input SolicitorSearch {
  q: String!
}

type SolicitorSearchResult {
  address: String
  name: String
  reference: String
}

union FMAResponse = ApplicationResponse | DipApplicationError

union FMAFields = FMAFieldsResponse | DipApplicationError

type FMAFieldsResponse {
  additional_fields_required: [ApplicationField!]
  dip_available: Boolean!
  messages: [String!]
  mortgage_id: String!
  permanent: Boolean!
  reason: String
  supports_dip_certificate: Boolean
}

input FMAInput {
  additional_fields: ApplicationFieldsInput
  case_id: ID!
  mortgage_id: ID!
  lender: ID!
}

type DipApplicationError {
  errors: [DipFieldError!]
  lender: String!
  status: ApplicationStatus
}

type DipFieldError {
  field: String
  message: String!
}
